import { useEffect, useState } from 'react';
import { NavigationMenu } from '../components/NavigationMenu';
import { useGeneration } from '../context/GenerationContext';
import { useTasks } from '../hooks/useTasks';
import { useNavigate } from 'react-router-dom';
import { tasksApi } from '../services/api';
import { LoadingAnimation } from '../components/LoadingAnimation';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { TaskItem } from '../components/TaskItem';
import { useInView } from 'react-intersection-observer';
import { Loader2 } from 'lucide-react';
import { GenerationTask } from '../types/generation';
import { PageHeader } from '../components/PageHeader';
import { useQueryClient } from '@tanstack/react-query';
import { TaskResultService } from '../services/taskResultService';
import { ErrorNotification } from '../components/ErrorNotification';

function TaskProgress() {
  useDocumentTitle('История задач');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { 
    cancelTask, 
    markAllTasksAsViewed
  } = useGeneration();
  const [retryingTasks, setRetryingTasks] = useState<Set<string>>(new Set());
  const [retryError, setRetryError] = useState<string | null>(null);
  
  // Используем новый хук с infinite query
  const { 
    data, 
    isLoading, 
    isError, 
    fetchNextPage, 
    hasNextPage, 
    isFetchingNextPage 
  } = useTasks();

  // Настройка intersection observer для автоподгрузки
  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: '200px'
  });

  // Получаем задачи с проверкой на undefined и фильтрацией дубликатов
  const rawTasks = data?.tasks || [];
  
  // Улучшенная фильтрация дубликатов с сохранением последней версии
  const tasksMap = new Map<string, GenerationTask>();
  
  rawTasks.forEach((task: GenerationTask) => {
    // Исключаем задачи без ID или с пустым ID
    if (!task.id || task.id.trim() === '') {
      console.warn('TaskProgress: Задача без корректного ID обнаружена и исключена:', task);
      return;
    }
    
    // Если задача уже есть в Map, сравниваем временные метки и берем более новую
    const existingTask = tasksMap.get(task.id);
    if (existingTask) {
      // Сравниваем по startedAt или по времени создания
      const taskTime = task.startedAt || 0;
      const existingTime = existingTask.startedAt || 0;
      
      if (taskTime > existingTime) {
        tasksMap.set(task.id, task);
      }
    } else {
      tasksMap.set(task.id, task);
    }
  });
  
  const tasks = Array.from(tasksMap.values());

  // Сортируем задачи по времени начала (новые сначала) для гарантии правильного порядка
  const sortedTasks = tasks.sort((a, b) => {
    const timeA = a.startedAt || 0;
    const timeB = b.startedAt || 0;
    return timeB - timeA; // Новые задачи первыми
  });

  const unviewedTasks = sortedTasks.filter((task: GenerationTask) => !task.isViewed && task.status !== 'pending');
  const safeHasNextPage = hasNextPage || false;

  // Автоподгрузка при скролле с обработкой ошибок
  useEffect(() => {
    if (inView && safeHasNextPage && !isFetchingNextPage) {
      fetchNextPage().catch((error: any) => {
        console.error('Ошибка при загрузке следующей страницы задач:', error);
        // Не показываем ошибку пользователю, просто логируем
      });
    }
  }, [inView, safeHasNextPage, isFetchingNextPage, fetchNextPage]);

  // SignalR обновления обрабатываются в GenerationContext, здесь только слушаем изменения
  // Удаляем дублирующие обработчики для улучшения производительности

  // Автоматическое помечание всех задач как просмотренных при загрузке страницы
  useEffect(() => {
    const markTasksAsViewedOnPageLoad = async () => {
      if (unviewedTasks.length > 0 && !isLoading) {
        try {
          const startTime = performance.now();
          await markAllTasksAsViewed();
          const endTime = performance.now();
          
          console.log(`✅ Помечено ${unviewedTasks.length} задач как просмотренные за ${(endTime - startTime).toFixed(2)}мс`);
        } catch (error) {
          console.error('Ошибка при автоматической пометке задач как просмотренных:', error);
        }
      }
    };

    markTasksAsViewedOnPageLoad();
  }, [unviewedTasks.length, isLoading, markAllTasksAsViewed]);

  // Мониторинг производительности рендеринга
  useEffect(() => {
    const renderStartTime = performance.now();
    
    return () => {
      const renderEndTime = performance.now();
      const renderTime = renderEndTime - renderStartTime;
      
      if (renderTime > 100) { // Если рендеринг занял больше 100мс
        console.warn(`⚠️ Медленный рендеринг TaskProgress: ${renderTime.toFixed(2)}мс для ${sortedTasks.length} задач`);
      }
    };
  }, [sortedTasks.length]);

  const handleCancelTask = async (taskId: string) => {
    try {
      await cancelTask(taskId);
    } catch (error) {
      console.error('Ошибка при отмене задачи:', error);
    }
  };

  const handleViewResult = async (taskId: string) => {
    // Находим задачу чтобы получить knowledgeDocumentId
    const task = sortedTasks.find((t: GenerationTask) => t.id === taskId);
    let knowledgeDocumentId = task?.knowledgeDocumentId;

    console.log('handleViewResult:', {
      taskId,
      task: task ? { 
        id: task.id, 
        title: task.title, 
        status: task.status,
        knowledgeDocumentId: task.knowledgeDocumentId,
        completedAt: task.completedAt,
        // Полные данные задачи для отладки
        fullTask: task
      } : null,
      knowledgeDocumentId,
      allTasks: sortedTasks.map((t: GenerationTask) => ({ id: t.id, title: t.title, knowledgeDocumentId: t.knowledgeDocumentId }))
    });

    // Если knowledgeDocumentId отсутствует или невалиден, пробуем получить через TaskResultService
    if (!knowledgeDocumentId || knowledgeDocumentId === 'root' || knowledgeDocumentId.trim() === '') {
      try {
        const resultInfo = await TaskResultService.getTaskResultInfo(taskId, knowledgeDocumentId);
        if (resultInfo?.documentId) {
          knowledgeDocumentId = resultInfo.documentId;
        }
      } catch (e) {
        console.warn('Не удалось получить knowledgeDocumentId через TaskResultService', e);
      }
    }

    // Проверяем, что knowledgeDocumentId существует и не является 'root' или пустым
    if (knowledgeDocumentId && knowledgeDocumentId !== 'root' && knowledgeDocumentId.trim() !== '') {
      console.log(`Переход к документу: /knowledge/${knowledgeDocumentId}`);
      navigate(`/knowledge/${knowledgeDocumentId}`);
    } else {
      console.log('Нет валидного knowledgeDocumentId, переход к /knowledge');
      navigate('/knowledge');
    }
  };

  const handleRetryTask = async (task: GenerationTask) => {
    // Проверяем, не выполняется ли уже перезапуск этой задачи
    if (retryingTasks.has(task.id)) {
      console.log('Перезапуск задачи уже выполняется:', task.id);
      return;
    }

    // Добавляем задачу в список перезапускаемых
    setRetryingTasks(prev => new Set(prev).add(task.id));
    setRetryError(null);
    
    try {
      // Ретрай "в один шаг" (бэкенд сам запускает повторное выполнение).
      await tasksApi.retryTask(task.id);
      
      console.log('Запрос на генерацию отправлен успешно');
      
      // Мягкое обновление статуса задачи на пендинг
      queryClient.setQueryData(['tasks'], (oldData: any) => {
        if (!oldData?.pages) return oldData;
        
        const updatedPages = oldData.pages.map((page: any) => {
          if (!page?.tasks) return page;
          
          const updatedTasks = page.tasks.map((t: GenerationTask) => {
            if (t.id === task.id) {
              return {
                ...t,
                status: 'pending' as const,
                progress: 0,
                startedAt: Date.now(),
                completedAt: undefined,
                error: undefined
              };
            }
            return t;
          });
          
          return { ...page, tasks: updatedTasks };
        });
        
        return { ...oldData, pages: updatedPages };
      });
      
      console.log('Задача успешно перезапущена:', task.id);
          } catch (error: any) {
        console.error('Ошибка при перезапуске задачи:', error);
        
        let errorMessage = 'Произошла ошибка при перезапуске задачи.';
        
        if (error?.response) {
          console.error('Ответ сервера с ошибкой:', error.response.data);
          const serverError = error.response.data?.message || error.response.data?.error;
          if (serverError) {
            errorMessage = `Ошибка сервера: ${serverError}`;
          } else {
            errorMessage = `Ошибка сервера (${error.response.status}): ${error.response.statusText}`;
          }
        } else if (error?.message) {
          errorMessage = error.message;
        }

        setRetryError(errorMessage);
    } finally {
      // Удаляем задачу из списка перезапускаемых с небольшой задержкой
      // чтобы предотвратить множественные клики
      setTimeout(() => {
        setRetryingTasks(prev => {
          const newSet = new Set(prev);
          newSet.delete(task.id);
          return newSet;
        });
      }, 1000);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-800 dark:to-gray-900 transition-colors duration-200 mobile-content-padding overflow-x-hidden">
        <div className="hidden lg:block">
          <NavigationMenu />
        </div>
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <LoadingAnimation />
        </div>
      </div>
    );
  }

  // Не показываем ошибку пользователю - вместо этого показываем пустое состояние
  // React Query автоматически попытается повторить запрос при восстановлении сети

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-800 dark:to-gray-900 transition-colors duration-200 mobile-content-padding overflow-x-hidden">
      {/* Навигационное меню для десктопа */}
      <div className="hidden lg:block">
        <NavigationMenu />
      </div>

      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <PageHeader
          title="История задач"
          subtitle="Отслеживайте прогресс генерации контента и открывайте результаты"
        />

        {retryError && (
          <div className="w-full max-w-4xl mx-auto mb-4">
            <ErrorNotification
              message={retryError}
              onClose={() => setRetryError(null)}
            />
          </div>
        )}

        {/* Индикатор состояния сети */}
        {isError && sortedTasks.length > 0 && (
          <div className="w-full max-w-4xl mx-auto mb-4">
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  Показаны сохраненные данные. Подключение к серверу будет восстановлено автоматически.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Основное содержимое */}
        <div className="w-full max-w-4xl mx-auto">
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 overflow-hidden">
            <div className="p-6">
              {sortedTasks.length === 0 ? (
                <div className="text-center py-12">
                  {isError ? (
                    <div className="space-y-3">
                      <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto">
                        <div className="w-3 h-3 bg-gray-400 rounded-full animate-pulse"></div>
                      </div>
                      <p className="text-gray-500 dark:text-gray-400">
                        Загружаем ваши задачи...
                      </p>
                      <p className="text-sm text-gray-400 dark:text-gray-500">
                        Проверяем подключение к серверу
                      </p>
                    </div>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400">
                      У вас пока нет задач
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {sortedTasks.map((task: GenerationTask, index: number) => (
                    <TaskItem 
                      key={`task-${task.id}-${index}`}
                      task={task}
                      onCancel={handleCancelTask}
                      onViewResult={handleViewResult}
                      onRetry={handleRetryTask}
                      isRetrying={retryingTasks.has(task.id)}
                    />
                  ))}
                  
                  {/* Элемент для отслеживания скролла */}
                  <div ref={ref} className="h-1" />
                  
                  {/* Индикатор загрузки следующей страницы */}
                  {isFetchingNextPage && (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TaskProgress;